import { SelectionChangedEvent } from "ag-grid-community";
import { CounterCustomerChargeEntity } from "domain/entity/CounterCustomerCharge/CounterCustomerChargeEntity";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { COUNTER_CUSTOMER_CHARGE_COL_DEF } from "presentation/constant/CounterCustomerCharge/UpdateExtensionPaymentColumnDefinition";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { ExtensionPaymentState } from "presentation/model/CounterCustomerCharge/CounterCustomerChargeMaintenanceModel";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { convertDateToDateTimeForComponent } from "presentation/utils/timeUtil";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { HPHButton, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, Sidebarheader } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const ExtensionPaymentPanel = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.Detail;
    const { editingEntity, extensionPaymentMap, selectedRows, previousPaidThroughDateMap, selectedPaidThroughDateMap } = counterCustomerChargeState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const gridRef: any = useRef(null);

    const vaildation = useCallback(() => {
        const currentDate = new Date();//dd-MM-yyyy HH:mm:ss
        let msg = null;
        extensionPaymentMap.forEach((value, key) => {
            if (!value) {
                msg = "Please input the Paid through date time.";
                return false;
            }
            const paidDate = new Date(value);
            if (paidDate && paidDate < currentDate) {
                msg = "The Paid through date time should not be earlier than the current system date.";
                return false;
            }
            let date = previousPaidThroughDateMap?.get(key);
            if (date && paidDate < new Date(date)) {
                msg = "The Paid through date time should not be earlier than max Paid through date " + convertDateToDateTimeForComponent(date) + ".";
                return false;
            }
        });
        if (msg) {
            messageBarVM.showError(msg);
            return false;
        }
        return true;
    }, [messageBarVM, extensionPaymentMap, previousPaidThroughDateMap]);

    const handleSaveClick = useCallback(async () => {
        if (!vaildation()) {
            return;
        }
        setIsLoading(true);
        const map: Map<string, CounterCustomerChargeEntity> = new Map();
        Array.from(extensionPaymentMap.keys()).forEach((key) => {
            let item = selectedRows?.find((row) => row.cntrNo === key);
            if (item) {
                item = {
                    ...item,
                    paidThroughDate: extensionPaymentMap.get(key) ?? null,
                }
                map.set(key, item);
            }
        });

        counterCustomerChargeVM.updateExtensionPayment(map).then((res) => {
            setIsLoading(false);
            counterCustomerChargeVM.onSearch(counterCustomerChargeState.searchCriteria, operatingUnit).finally(() => {
                if (res && res.success) {
                    messageBarVM.showSuccess('Save Successfully.');
                    counterCustomerChargeVM.onMoreClose();
                } else {
                    messageBarVM.showError('Save Failed.');
                    counterCustomerChargeVM.onMoreClose();
                }
            });
        });

    }, [counterCustomerChargeVM, messageBarVM, counterCustomerChargeState.searchCriteria, vaildation, extensionPaymentMap, selectedRows, operatingUnit]);

    const handClose = useCallback(() => {
        counterCustomerChargeVM.onMoreClose();
    }, [counterCustomerChargeVM]);

    const allRows = useMemo<ExtensionPaymentState[]>(() => {
        if (!extensionPaymentMap || extensionPaymentMap.size === 0) {
            return [];
        }
        return Array.from(extensionPaymentMap)
            .map(([containerNo, paidThroughDate]) => ({
                cntrNo: containerNo,
                previousPaidThroughDate: previousPaidThroughDateMap?.get(containerNo) ?? null,
                paidThroughDate: paidThroughDate,
            }))
            .sort((a, b) => a.cntrNo.localeCompare(b.cntrNo));
    }, [extensionPaymentMap, previousPaidThroughDateMap]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        counterCustomerChargeVM.updateSelectedPaidThroughDateRows(selectedRows);
    }, [counterCustomerChargeVM])

    const handApply = useCallback(() => {
        if (!selectedPaidThroughDateMap || selectedPaidThroughDateMap.size === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        if (!editingEntity.paidThroughDate) {
            messageBarVM.showError("Please input the Paid through date time.");
            return;
        }
        counterCustomerChargeVM.onApplyExtensionPaymenClick(extensionPaymentMap, selectedPaidThroughDateMap);
    }, [counterCustomerChargeVM, editingEntity.paidThroughDate, messageBarVM, extensionPaymentMap, selectedPaidThroughDateMap]);

    const updatePaidThroughDatePanelTable =
        <NbisTable
            id='update-paid-through-date-table'
            isNewColumnSetting={false}
            columns={COUNTER_CUSTOMER_CHARGE_COL_DEF?.slice()}
            data={allRows ?? []}
            showPaginator={false}
            editable={false}
            showAddIcon={false}
            showUploadIcon={false}
            showDeleteButton={false}
            showReloadIcon={false}
            selectionMode={false}
            isRowHighligted={true}
            showPreferenceMenu={false}
            showSidebar={false}
            handleSelectionChanged={handleSelectionChange}
            gridHeight="customHeight"
            customHeight="calc(40vh)"
            ref={gridRef}
            rowSelection={"multiple"}
        />

    const memoExtensionPayment = useMemo(() => {
        if (!extensionPaymentMap || extensionPaymentMap.size === 0) {
            return null;
        }

        return (
            <>
                <CriteriaItemContainer>
                    <div className="im-flex-row-item" style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
                        <NbisDateTimePickerComponent
                            disabled={false}
                            label={COUNTER_CUSTOMER_CHARGE_CONSTANT.PAID_THROUGH_DATE}
                            width="220px"
                            date={editingEntity?.paidThroughDate}
                            fieldName={"paidThroughDate"}
                            errorMessage=""
                            onDateChange={counterCustomerChargeVM.onCounterCustomerFieldChange}
                        />
                    </div>
                    <HPHButton label={'Apply'} size={'Small'} theme={'Secondary'} onClick={handApply} />
                </CriteriaItemContainer>
            </>
        );
    }, [COUNTER_CUSTOMER_CHARGE_CONSTANT.PAID_THROUGH_DATE, counterCustomerChargeVM, editingEntity?.paidThroughDate, handApply, extensionPaymentMap]);

    return (<div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <HeaderTitle>{"Extension Payment"}</HeaderTitle>
                </Sidebarheader>
            </div>
        </div>

        {isLoading && <Loader Indicator="Spinner" size="Medium" />}

        <div style={{ marginTop: "-50px" }}>
            <TableWrapper>
                <div style={{ marginLeft: "15px" }}>{updatePaidThroughDatePanelTable}</div>
            </TableWrapper>
        </div>

        <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
            <div className="flex-row-auto">
                <div className={'flex-row-item'}>
                    {memoExtensionPayment}
                </div>
            </div>
        </div>
        <SidebarActionBar>
            <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={handClose} />
            <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={() => handleSaveClick()} />
        </SidebarActionBar>
    </div>)
}

export default memo(ExtensionPaymentPanel);